import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { TONE_LABELS, pathFor } from './steps'

/** The values the API's `Literal` admits. Their labels live in `steps.ts`. */
const TONES = ['professional_warm', 'direct_technical', 'friendly_casual', 'formal'] as const

const VIEWPOINTS = [
  { value: 'we', label: 'We / our team' },
  { value: 'i', label: 'I / my studio' },
  { value: 'third_person', label: 'Third person' },
] as const

type Tone = (typeof TONES)[number]
type Viewpoint = (typeof VIEWPOINTS)[number]['value']
type Sample = Schema<'SampleResponse'>

interface Settings {
  tone: Tone
  viewpoint: Viewpoint
  avoid_words: string
  always_mention: string
}

/**
 * Step 6 — S-D2's `s6`.
 *
 * **The sample regenerates as the settings change**, because voice is chosen by
 * reading actual output rather than by picking an adjective and hoping. That is
 * S-D2's stated reason for the panel, and it is why the preview is its own route:
 * `POST /brand-voice/sample` does not save, so trying a voice on is free and
 * Back is not the only way to undo an experiment.
 *
 * The caption names what produced the sample, read from the response. Until
 * S-B25 that is `template`, and printing "Sonnet 5" beside text no model wrote
 * would be the kind of small lie that makes a tenant doubt the rest of the
 * screen.
 *
 * If a sample uses a word the tenant banned, it is **shown with the words named**
 * rather than hidden. An empty panel would leave them with no idea which rule
 * they had just written. The check is the server's, so it will still be made
 * when a model is writing these.
 */
/**
 * What produced the live sample, in the reader's words rather than the wire's
 * (ONB-41).
 *
 * Unknown values pass through unchanged: a model id this build has never heard
 * of is still more informative in the caption than nothing, and it is what a
 * deploy mid-release looks like.
 */
const SOURCE_LABELS: Record<string, string> = {
  template: 'a fixed template',
  // The spellings the API actually sends. **Sonnet has no date suffix and Haiku
  // does**, and that asymmetry is deliberate on the server: `config.py:305-311`
  // pins a dated Haiku snapshot and says in so many words that the
  // `claude-haiku-4-5` alias is not used, because an alias that rolls would
  // change a tenant's results on a day nobody deployed. Anthropic publishes
  // Sonnet 5 without one, so `content_ai_model` carries it bare
  // (`config.py:356-361`).
  //
  // The first pass keyed this on the undated Haiku alias — the one spelling the
  // codebase exists to avoid — so the label would have missed and printed the
  // raw id. Found reverifying. Both are listed now: the dated snapshot because
  // it is what is sent, the alias because a future `content_ai_model` could
  // legitimately carry it and a caption is not worth a second defect.
  'claude-sonnet-5': 'Sonnet 5',
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
  'claude-haiku-4-5': 'Haiku 4.5',
}

/** The raw id when it is not one we name — more use in a caption than a blank. */
function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source
}

export function BrandVoiceStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const [settings, setSettings] = useState<Settings>(() => ({
    tone: (state.brand_voice?.tone as Tone | undefined) ?? 'professional_warm',
    viewpoint: (state.brand_voice?.viewpoint as Viewpoint | undefined) ?? 'we',
    avoid_words: state.brand_voice?.avoid_words.join(', ') ?? '',
    always_mention: state.brand_voice?.always_mention.join(', ') ?? '',
  }))
  const [sample, setSample] = useState<Sample | null>(null)

  const preview = useMutation((body: Settings) =>
    call(() => api.POST('/api/v1/onboarding/brand-voice/sample', { body })),
  )
  const save = useMutation((body: Settings) =>
    call(() => api.PUT('/api/v1/onboarding/brand-voice', { body })),
  )

  async function regenerate(next: Settings): Promise<void> {
    const result = await preview.run(next)
    if (result.ok) setSample(result.data)
  }

  // One sample on arrival. Afterwards it is *Regenerate*, not an effect on every
  // keystroke: once S-B25 makes this a model call, a sample per character typed
  // into "words to avoid" would be a bill nobody asked for.
  const latest = useRef(settings)
  latest.current = settings
  const asked = useRef(false)
  useEffect(() => {
    if (asked.current) return
    asked.current = true
    void regenerate(latest.current)
    // `regenerate` closes over the mutation, which is stable for the component's
    // life; the guard above is what makes this run once regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function update(patch: Partial<Settings>): void {
    setSettings((current) => ({ ...current, ...patch }))
    save.reset()
  }

  async function onFinish(): Promise<void> {
    const result = await save.run(settings)
    if (result.ok) {
      adopt(result.data)
      // Settings consolidation (2026-09-15): five settings steps follow, so
      // this is no longer the finish — the cursor says where next.
      void navigate(pathFor(result.data.current_step))
    }
  }

  return (
    <div className="app-wiz">
      {/* ONB-33 dropped the rail here while this was the last step. It is
          step 6 of 11 since 2026-09-15; `ApprovalsStep` is the finish now and
          carries that treatment. */}
      <WizardChrome current="brand_voice" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">How should we sound?</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              Everything we write follows this. You can change it any time and it applies to the
              next draft.
            </p>
          </div>

          <InlineError failure={save.state.failure ?? preview.state.failure} />

          <div className="ar-grid ar-grid--2">
            <div className="ar-stack">
              <Field label="Tone">
                {(aria) => (
                  <select
                    className="ar-select"
                    {...aria}
                    value={settings.tone}
                    onChange={(event) => {
                      update({ tone: event.target.value as Tone })
                    }}
                  >
                    {TONES.map((tone) => (
                      <option key={tone} value={tone}>
                        {TONE_LABELS[tone]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              <Field label="Point of view">
                {(aria) => (
                  <select
                    className="ar-select"
                    {...aria}
                    value={settings.viewpoint}
                    onChange={(event) => {
                      update({ viewpoint: event.target.value as Viewpoint })
                    }}
                  >
                    {VIEWPOINTS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              <TextField
                label="Words to avoid"
                help="Comma separated. We'll never use these."
                placeholder="cheap, budget, world-class"
                value={settings.avoid_words}
                onChange={(event) => {
                  update({ avoid_words: event.target.value })
                }}
              />

              {/* ONB-40. No help text, which is what S-D2 draws. The line
                  above it carries the "Comma separated" convention for the pair,
                  and repeating it under the second box says nothing the first has
                  not already said — a caption per field is how a four-field
                  column becomes eight lines of reading. */}
              <TextField
                label="Always mention"
                placeholder="10-year warranty, in-house carpentry"
                value={settings.always_mention}
                onChange={(event) => {
                  update({ always_mention: event.target.value })
                }}
              />
            </div>

            <div className="ar-panel ar-stack ar-stack--tight">
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>Live sample</strong>
                <Button
                  tone="quiet"
                  size="sm"
                  busy={preview.pending}
                  busyLabel="Writing a sample"
                  onClick={() => {
                    void regenerate(settings)
                  }}
                >
                  Regenerate
                </Button>
              </div>

              {sample ? (
                <>
                  <p
                    style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)', lineHeight: 1.7 }}
                  >
                    {sample.text}
                  </p>
                  {(sample.avoided_words_used ?? []).length > 0 ? (
                    <div className="ar-alert ar-alert--warning" role="alert">
                      {/* ONB-34. `⚠` cautions — the sample can be regenerated. */}
                      <span className="ar-alert__icon" aria-hidden="true">
                        ⚠
                      </span>
                      <div className="ar-alert__body">
                        <strong>This sample used a word you asked us to avoid</strong>
                        {(sample.avoided_words_used ?? []).join(', ')} — regenerate, or drop the
                        rule.
                      </div>
                    </div>
                  ) : null}
                  <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
                    {/* ONB-41. S-D2's caption reads "· Sonnet 5" and the field is
                        a wire value — `template` today, a model id once S-B25
                        makes this a model call. Named here so the caption reads
                        as a product name rather than as a slug the moment the
                        value changes, and so an unrecognised one still reads as
                        itself rather than as a blank. */}
                    Generated with your current settings · {sourceLabel(sample.source)}
                  </span>
                </>
              ) : (
                <div className="ar-skeleton ar-skeleton--text" aria-hidden="true" />
              )}
            </div>
          </div>
        </div>

        {/* ONB-19. S-D2's `s6` footer is Back + one primary, with no Skip:
            the press saves whatever the four controls hold — all of which have
            defaults — so it settles the step either way, and a Skip beside it
            offers the same outcome under a word that suggests something was
            given up. It read *Finish setup* while this was the last step;
            since 2026-09-15 it is *Continue*. See `WizardFooter.offerSkip`. */}
        <WizardFooter current="brand_voice" offerSkip={false}>
          <Button
            tone="primary"
            busy={save.pending}
            busyLabel="Saving your voice"
            onClick={() => {
              void onFinish()
            }}
          >
            Continue
          </Button>
        </WizardFooter>
      </section>
    </div>
  )
}
